/**
 * Aggregator competitive intel — how our competitor brands rank on
 * Google / Swiggy / Zomato per locality, versus Flax.
 *
 * Phase 1: Google Places rating + review count, per outlet, daily snapshot.
 * Phase 2: Swiggy / Zomato scrape (rating, delivery ETA, offers, menu).
 */
import { useMemo, useState } from 'react';
import {
  Building2,
  ExternalLink,
  MapPin,
  MessageSquare,
  Plus,
  Rocket,
  Star,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MotionCard } from '@/components/motion/MotionCard';
import { formatNumberCompact } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useCompetitorBrands,
  useCompetitorOutlets,
  useLatestRatings,
  type CompetitorBrand,
  type CompetitorOutlet,
  type RatingSnapshot,
} from '@/hooks/useAggregatorCompetitive';
import { AddOutletDialog } from './AddOutletDialog';

export function AggregatorsCompetitivePage() {
  const { data: brands = [] } = useCompetitorBrands();
  const { data: outlets = [], isLoading: outletsLoading } = useCompetitorOutlets();
  const { data: ratings = [] } = useLatestRatings(30);
  const [showAdd, setShowAdd] = useState(false);
  const [prefillBrand, setPrefillBrand] = useState<string | undefined>();

  const cities = useMemo(() => {
    const set = new Set(outlets.map((o) => o.city));
    return Array.from(set).sort();
  }, [outlets]);
  const [activeCity, setActiveCity] = useState<string>('all');

  const latestByOutlet = useMemo(() => {
    const map = new Map<string, RatingSnapshot>();
    for (const r of ratings) {
      const key = `${r.outlet_id}::${r.source}`;
      if (!map.has(key)) map.set(key, r);
    }
    return map;
  }, [ratings]);

  const priorByOutlet = useMemo(() => {
    const seen = new Set<string>();
    const map = new Map<string, RatingSnapshot>();
    for (const r of ratings) {
      const key = `${r.outlet_id}::${r.source}`;
      if (!seen.has(key)) {
        seen.add(key);
        continue;
      }
      if (!map.has(key)) map.set(key, r);
    }
    return map;
  }, [ratings]);

  const visibleOutlets = useMemo(
    () => (activeCity === 'all' ? outlets : outlets.filter((o) => o.city === activeCity)),
    [outlets, activeCity],
  );

  const byBrand = useMemo(() => {
    const groups = new Map<string, CompetitorOutlet[]>();
    for (const o of visibleOutlets) {
      const arr = groups.get(o.brand_slug) ?? [];
      arr.push(o);
      groups.set(o.brand_slug, arr);
    }
    return groups;
  }, [visibleOutlets]);

  // Headline stats — combine Google + Zomato per outlet
  const headline = useMemo(() => {
    let sumRating = 0;
    let ratedCount = 0;
    let totalReviews = 0;
    let sources = 0;
    let bestMover: { brand: string; delta: number; locality: string; source: string } | null = null;
    for (const o of visibleOutlets) {
      const g = latestByOutlet.get(`${o.id}::google`);
      const z = latestByOutlet.get(`${o.id}::zomato`);
      const rs = [g?.rating, z?.rating].filter((r): r is number => r != null);
      if (rs.length > 0) {
        sumRating += rs.reduce((a, b) => a + b, 0) / rs.length;
        ratedCount++;
        sources += rs.length;
      }
      totalReviews += Math.max(g?.review_count ?? 0, z?.review_count ?? 0);
      // Best mover from either source
      for (const [latest, prior, src] of [
        [g, priorByOutlet.get(`${o.id}::google`), 'google'] as const,
        [z, priorByOutlet.get(`${o.id}::zomato`), 'zomato'] as const,
      ]) {
        if (latest?.rating != null && prior?.rating != null) {
          const d = latest.rating - prior.rating;
          if (Math.abs(d) < 0.05) continue;
          if (!bestMover || Math.abs(d) > Math.abs(bestMover.delta)) {
            bestMover = { brand: o.brand_slug, delta: d, locality: o.locality, source: src };
          }
        }
      }
    }
    return {
      avgRating: ratedCount > 0 ? sumRating / ratedCount : null,
      totalReviews,
      ratedCount,
      totalOutlets: visibleOutlets.length,
      sources,
      bestMover,
      worstMover: bestMover, // legacy key kept for compat
    };
  }, [visibleOutlets, latestByOutlet, priorByOutlet]);

  function openAdd(slug?: string) {
    setPrefillBrand(slug);
    setShowAdd(true);
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-primary" />
            Aggregators — competitive intel
          </span>
        }
        description="Ratings, review velocity, and (soon) offers + menu deltas for tracked competitor restaurants on Google / Swiggy / Zomato."
        actions={
          !showAdd ? (
            <Button size="sm" onClick={() => openAdd()}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add competitor outlet
            </Button>
          ) : null
        }
      />

      <AddOutletDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        brands={brands}
        initialBrandSlug={prefillBrand}
      />

      {/* Headline stats */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          icon={<Building2 className="h-3.5 w-3.5" />}
          label="Outlets tracked"
          value={String(headline.totalOutlets)}
          sub={`across ${byBrand.size} of ${brands.length} brands`}
          tone="default"
        />
        <Stat
          icon={<Star className="h-3.5 w-3.5" />}
          label="Cohort avg rating"
          value={headline.avgRating != null ? headline.avgRating.toFixed(2) + '★' : '—'}
          sub={`${headline.ratedCount} outlet${headline.ratedCount === 1 ? '' : 's'} with data`}
          tone="amber"
        />
        <Stat
          icon={<MessageSquare className="h-3.5 w-3.5" />}
          label="Total reviews"
          value={formatNumberCompact(headline.totalReviews)}
          sub="across all tracked outlets"
          tone="default"
        />
        <Stat
          icon={
            headline.bestMover ? (
              <TrendingUp className="h-3.5 w-3.5" />
            ) : (
              <TrendingDown className="h-3.5 w-3.5" />
            )
          }
          label="Biggest 24h move"
          value={
            headline.bestMover
              ? `${headline.bestMover.delta > 0 ? '+' : ''}${headline.bestMover.delta.toFixed(2)}`
              : '—'
          }
          sub={
            headline.bestMover
              ? `@${headline.bestMover.brand} · ${headline.bestMover.locality}`
              : 'need 2+ syncs to compare'
          }
          tone={headline.bestMover && headline.bestMover.delta > 0 ? 'emerald' : 'rose'}
        />
      </div>

      {cities.length > 0 && (
        <div className="-mx-1 flex gap-1 overflow-x-auto pb-1 pl-1">
          <CityTab
            active={activeCity === 'all'}
            onClick={() => setActiveCity('all')}
            label={`All (${outlets.length})`}
          />
          {cities.map((c) => (
            <CityTab
              key={c}
              active={activeCity === c}
              onClick={() => setActiveCity(c)}
              label={`${c} (${outlets.filter((o) => o.city === c).length})`}
            />
          ))}
        </div>
      )}

      {outletsLoading ? (
        <div className="rounded-md border border-border/40 bg-muted/20 p-8 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {brands.map((brand) => (
            <BrandCard
              key={brand.slug}
              brand={brand}
              outlets={byBrand.get(brand.slug) ?? []}
              latestByOutlet={latestByOutlet}
              priorByOutlet={priorByOutlet}
              onAddOutlet={() => openAdd(brand.slug)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CityTab({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'shrink-0 whitespace-nowrap rounded-t-md px-3 py-1.5 text-2xs font-medium transition',
        active
          ? 'border-x border-t border-border/60 bg-card text-foreground shadow-sm'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

function Stat({
  icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  tone: 'default' | 'amber' | 'emerald' | 'rose';
}) {
  const toneClass =
    tone === 'amber'
      ? 'text-amber-500'
      : tone === 'emerald'
        ? 'text-emerald-500'
        : tone === 'rose'
          ? 'text-rose-500'
          : 'text-muted-foreground';
  return (
    <div className="rounded-md border border-border/40 bg-card/60 p-3">
      <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
        <span className={toneClass}>{icon}</span>
        {label}
      </div>
      <div className={cn('mt-1 text-xl font-semibold tabular-nums', tone !== 'default' && toneClass)}>
        {value}
      </div>
      <div className="mt-0.5 text-2xs text-muted-foreground">{sub}</div>
    </div>
  );
}

function BrandCard({
  brand,
  outlets,
  latestByOutlet,
  priorByOutlet,
  onAddOutlet,
}: {
  brand: CompetitorBrand;
  outlets: CompetitorOutlet[];
  latestByOutlet: Map<string, RatingSnapshot>;
  priorByOutlet: Map<string, RatingSnapshot>;
  onAddOutlet: () => void;
}) {
  // Brand-level aggregate — average across ALL sources per outlet
  const brandAgg = useMemo(() => {
    let sum = 0;
    let count = 0;
    let reviews = 0;
    for (const o of outlets) {
      const g = latestByOutlet.get(`${o.id}::google`);
      const z = latestByOutlet.get(`${o.id}::zomato`);
      const rs = [g?.rating, z?.rating].filter((r): r is number => r != null);
      if (rs.length > 0) {
        sum += rs.reduce((a, b) => a + b, 0) / rs.length;
        count++;
      }
      // Prefer larger review count between Google (permanent) and Zomato (aggregator).
      const rc = Math.max(g?.review_count ?? 0, z?.review_count ?? 0);
      if (rc > 0) reviews += rc;
    }
    return { avg: count > 0 ? sum / count : null, reviews, count };
  }, [outlets, latestByOutlet]);

  return (
    <MotionCard>
      <Card className={cn(outlets.length === 0 && 'border-dashed opacity-70 hover:opacity-100')}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-start justify-between gap-2 text-sm">
            <div className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                {brand.display_name}
              </span>
              {brand.ig_username && (
                <span className="text-2xs font-normal text-muted-foreground">
                  @{brand.ig_username}
                </span>
              )}
            </div>
            {brandAgg.avg != null && (
              <div className="text-right">
                <div className="flex items-center gap-0.5 text-base font-semibold tabular-nums">
                  <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                  {brandAgg.avg.toFixed(1)}
                </div>
                <div className="text-2xs font-normal text-muted-foreground">
                  {formatNumberCompact(brandAgg.reviews)} reviews
                </div>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 pt-0">
          {outlets.length === 0 ? (
            <button
              type="button"
              onClick={onAddOutlet}
              className="flex w-full flex-col items-center gap-1 rounded-md border border-dashed border-border/40 bg-muted/20 p-4 text-2xs text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <Plus className="h-4 w-4" />
              Add {brand.display_name} outlet
            </button>
          ) : (
            outlets.map((o) => (
              <OutletRow
                key={o.id}
                outlet={o}
                google={latestByOutlet.get(`${o.id}::google`)}
                googlePrior={priorByOutlet.get(`${o.id}::google`)}
                zomato={latestByOutlet.get(`${o.id}::zomato`)}
                zomatoPrior={priorByOutlet.get(`${o.id}::zomato`)}
              />
            ))
          )}
        </CardContent>
      </Card>
    </MotionCard>
  );
}

function OutletRow({
  outlet,
  google,
  googlePrior,
  zomato,
  zomatoPrior,
}: {
  outlet: CompetitorOutlet;
  google: RatingSnapshot | undefined;
  googlePrior: RatingSnapshot | undefined;
  zomato: RatingSnapshot | undefined;
  zomatoPrior: RatingSnapshot | undefined;
}) {
  const [showDetail, setShowDetail] = useState(false);
  const hasPlace = !!outlet.google_place_id;
  const mapsUrl = hasPlace
    ? `https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${outlet.google_place_id}`
    : null;

  const photos = zomato?.photos ?? null;
  const badges = zomato?.badges ?? null;
  const mustTry = zomato?.must_try ?? null;
  const address = zomato?.raw?.address;
  const cuisines = zomato?.raw?.servesCuisine;
  const cuisineList = Array.isArray(cuisines)
    ? cuisines
    : typeof cuisines === 'string'
      ? cuisines.split(',').map((c) => c.trim()).filter(Boolean)
      : [];
  const anyDetail =
    !!photos?.length ||
    !!badges?.length ||
    !!mustTry?.length ||
    !!zomato?.cost_for_two ||
    !!address ||
    cuisineList.length > 0;

  return (
    <div className="rounded-md border border-border/30 bg-card/60 px-2 py-1.5">
      {/* Locality — dedicated row so it never collapses */}
      <button
        type="button"
        onClick={() => anyDetail && setShowDetail((v) => !v)}
        className={cn(
          'flex w-full items-center gap-1 text-left text-2xs',
          anyDetail && 'hover:text-foreground',
        )}
      >
        <MapPin className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />
        <span className="truncate font-medium text-foreground">{outlet.locality}</span>
        {anyDetail && (
          <span className="ml-auto text-muted-foreground">{showDetail ? '▾' : '▸'}</span>
        )}
      </button>

      {/* Ratings row */}
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-2xs">
        <SourceRating
          label="G"
          color="text-blue-500"
          latest={google}
          prior={googlePrior}
          fallback={hasPlace ? '…' : 'no id'}
        />
        <SourceRating
          label="Z"
          color="text-rose-500"
          latest={zomato}
          prior={zomatoPrior}
          fallback={outlet.zomato_url ? '…' : null}
        />
        {zomato?.cost_for_two != null && (
          <span className="rounded bg-muted px-1 py-0.5 text-2xs text-muted-foreground tabular-nums" title="Cost for two">
            ₹{zomato.cost_for_two}
          </span>
        )}

        <div className="flex shrink-0 items-center gap-1">
          {mapsUrl && (
            <a href={mapsUrl} target="_blank" rel="noreferrer" title="Google Maps"
               className="rounded p-0.5 text-blue-500 hover:bg-blue-500/10">
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
          {outlet.swiggy_url && (
            <a href={outlet.swiggy_url} target="_blank" rel="noreferrer" title="Swiggy"
               className="rounded px-1 py-0.5 font-medium text-orange-500 hover:bg-orange-500/10">
              S
            </a>
          )}
          {outlet.zomato_url && (
            <a href={outlet.zomato_url} target="_blank" rel="noreferrer" title="Zomato"
               className="rounded px-1 py-0.5 font-medium text-rose-500 hover:bg-rose-500/10">
              Z
            </a>
          )}
        </div>
      </div>

      {/* Expanded panel — photos, badges, must-try */}
      {showDetail && anyDetail && (
        <div className="mt-2 space-y-2 border-t border-border/30 pt-2">
          {address && (
            <div className="text-2xs text-muted-foreground">
              📍{' '}
              {[address.streetAddress, address.addressLocality, address.postalCode]
                .filter(Boolean)
                .join(', ')}
            </div>
          )}
          {cuisineList.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {cuisineList.slice(0, 8).map((c) => (
                <span
                  key={c}
                  className="rounded-full bg-muted px-2 py-0.5 text-2xs text-muted-foreground"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
          {badges && badges.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {badges.map((b, i) => (
                <span
                  key={i}
                  className="rounded-full bg-amber-500/10 px-2 py-0.5 text-2xs font-medium text-amber-700 dark:text-amber-400"
                >
                  🏅 {b}
                </span>
              ))}
            </div>
          )}
          {mustTry && mustTry.length > 0 && (
            <div>
              <div className="mb-1 text-2xs text-muted-foreground">Must-try dishes</div>
              <div className="flex flex-wrap gap-1">
                {mustTry.slice(0, 8).map((m, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-2xs text-emerald-700 dark:text-emerald-400"
                  >
                    {m}
                  </span>
                ))}
              </div>
            </div>
          )}
          {photos && photos.length > 0 && (
            <div>
              <div className="mb-1 text-2xs text-muted-foreground">
                {photos.length} photos from Zomato
              </div>
              <div className="flex gap-1 overflow-x-auto">
                {photos.slice(0, 10).map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt=""
                    loading="lazy"
                    className="h-16 w-16 shrink-0 rounded-md object-cover"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SourceRating({
  label,
  color,
  latest,
  prior,
  fallback,
}: {
  label: string;
  color: string;
  latest: RatingSnapshot | undefined;
  prior: RatingSnapshot | undefined;
  fallback: string | null;
}) {
  if (latest?.rating == null) {
    if (!fallback) return null;
    return (
      <span className="flex items-center gap-1 text-muted-foreground tabular-nums">
        <span className={cn('font-semibold', color)}>{label}</span>
        <span className="text-2xs">{fallback}</span>
      </span>
    );
  }
  const delta =
    latest.rating != null && prior?.rating != null ? latest.rating - prior.rating : null;
  return (
    <span className="flex items-center gap-1 tabular-nums">
      <span className={cn('text-2xs font-semibold', color)}>{label}</span>
      <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" />
      <span className="font-semibold">{latest.rating.toFixed(1)}</span>
      {latest.review_count != null && (
        <span className="text-muted-foreground">({formatNumberCompact(latest.review_count)})</span>
      )}
      {delta != null && Math.abs(delta) >= 0.05 && (
        <span className={cn(delta > 0 ? 'text-emerald-500' : 'text-rose-500')}>
          {delta > 0 ? '+' : ''}
          {delta.toFixed(2)}
        </span>
      )}
    </span>
  );
}

